import React from 'react';
import ExternalLink from '../shared/ExternalLink';
import { getAgentDesktopURL } from '../shared/utils/Utils';

const answersLink = <ExternalLink to={getAgentDesktopURL('kb-admin')} label="Answers" />;

const serviceFeesHelpDoc = (
  <ExternalLink to="https://help.gladly.com/implementation/docs/invoice-and-billing-overview#payperuse-invoices" />
);

export const FIELD_MAPPER = Object.freeze({
  NAME: {
    path: 'name',
    label: 'Thread name',
    defaultValue: '',
    description: 'We recommend the name describes the type of Self-Service conversation. For example, Order Status.',
    placeholder: 'Enter the name',
  },

  CUSTOMER_IDENTITY_AUTHENTICATION_CONFIRM_CUSTOMER_AUTH_CODE: {
    path: 'nodesMap.authentication_code_prompt.ivrNode.message.message',
    label: 'Confirming a customer’s authentication code',
    description:
      'Ask the customer to provide the unique authentication code they received via email. You can use the email address variable in your message by adding <EMAILADDRESS>.',
    placeholder: 'Message',
    defaultValue:
      'Got it! To confirm it’s you, please type in the code that was sent to the email address <EMAILADDRESS>.',
  },
  CUSTOMER_IDENTITY_AUTHENTICATION_CORRECT_CODE_MESSAGE: {
    path: 'nodesMap.auth_code_success.ivrNode.message.message',
    label: 'When the customer provides the correct code',
    description: '',
    placeholder: 'Message',
    defaultValue: 'Thanks! We have successfully verified your account.',
  },
  CUSTOMER_IDENTITY_AUTHENTICATION_INCORRECT_CODE_MESSAGE: {
    path: 'nodesMap.auth_code_failure.ivrNode.message.message',
    label: 'When the customer doesn’t provide the correct code',
    description: '',
    placeholder: 'Message',
    defaultValue:
      'Oops, looks like the code you provided is incorrect. But don’t worry, one of our agents will be with you shortly to help.',
  },
  AUTHENTICATE_CUSTOMER_EMAIL_ANSWER_ID: {
    path: 'nodesMap.email_auth_code.ivrNode.answerId',
    label: 'Email message',
    description: (
      <>
        Select an Email Answer that will contain the authentication code. If you haven’t created the email message for
        this Thread,
        <br /> go to {answersLink}.
      </>
    ),
    placeholder: 'Select an Answer',
    defaultValue: undefined,
  },
  AUTHENTICATE_CUSTOMER_EMAIL_ANSWER_LANGUAGE: {
    path: 'nodesMap.email_auth_code.ivrNode.answerLanguage',
    label: 'Language',
    placeholder: 'Select a Language',
    defaultValue: 'en-us',
  },
  AUTHENTICATE_CUSTOMER_EMAIL_FROM: {
    path: 'nodesMap.email_auth_code.ivrNode.emailFromAddress',
    label: 'From email address',
    description: '',
    placeholder: 'Select an email address',
    defaultValue: undefined,
  },
  ORDERS_FOUND: {
    path: 'nodesMap.orders_found.ivrNode.message.message',
    label: 'When an order is found',
    description: '',
    placeholder: 'Message',
    defaultValue: 'Here’s the status of your recent order:',
  },
  NO_ORDERS_FOUND: {
    path: 'nodesMap.no_orders_found.ivrNode.message.message',
    label: 'When no order can be found',
    description: '',
    placeholder: 'Message',
    defaultValue:
      'We were unable to find an order associated with that email address. But don’t worry, one of our agents will be with you shortly to help.',
  },
  MISSING_FULFILLMENT_MESSAGE: {
    path: 'nodesMap.display_order.ivrNode.config.missingFulfillmentMessage',
    label: 'When the order card is missing the status, estimated arrival date, and tracking information',
    description: 'This will appear in the order card.',
    placeholder: 'Message',
    defaultValue: 'We’re preparing your order for shipment.',
  },
  MISSING_STATUS_AND_ETA_MESSAGE: {
    path: 'nodesMap.display_order.ivrNode.config.missingStatusAndEtaMessage',
    label: 'When the order card is missing the status and estimated arrival date',
    description: 'This will appear in the order card.',
    placeholder: 'Message',
    defaultValue: 'We’re awaiting carrier pickup.',
  },
  MISSING_ETA_MESSAGE: {
    path: 'nodesMap.display_order.ivrNode.config.missingEtaMessage',
    label: 'When the order card is missing the estimated arrival date',
    description: 'This will appear in the order card.',
    placeholder: 'Message',
    defaultValue: 'We’ll post the estimated arrival date soon.',
  },
  VERIFY_SUCCESS: {
    path: 'nodesMap.verify_success.ivrNode.message.message',
    label: 'Ask the customer if the question has been answered',
    description: 'This will appear after the order card has been shown.',
    placeholder: 'Message',
    defaultValue: 'Did this answer your question?',
  },
  ORDER_STATUS_HAS_BEEN_SHOWN_QUICK_REPLY_YES_THATS_HELPED: {
    description: (
      <>
        This will appear as a quick reply button. Clicking this button will result in a resolution. To read more about
        Self-Service fees, see our <br /> {serviceFeesHelpDoc}.
      </>
    ),
    path: 'nodesMap.verify_success.ivrNode.menu[0].label',
    label: 'When the question has been answered',
    defaultValue: 'Yes, it did',
    placeholder: 'Message',
  },

  ORDER_STATUS_HAS_BEEN_SHOWN_QUICK_REPLY_TO_HERO: {
    description: 'This will appear as a quick reply button. Clicking this button will route the customer to an agent.',
    path: 'nodesMap.verify_success.ivrNode.menu[1].label',
    label: 'When the question has not been answered',
    placeholder: 'Message',
    defaultValue: 'No, speak to an agent',
  },

  ASK_THERE_ANYTHING_ELSE_YOU_NEED: {
    description: '',
    path: 'nodesMap.next_steps.ivrNode.message.message',
    label: 'Ask the customer if they need more help',
    placeholder: 'Message',
    defaultValue: 'Is there anything else you need help with?',
  },

  ASK_THERE_ANYTHING_ELSE_YOU_NEED_QUICK_REPLY_GOODBY: {
    description: 'This will appear as a quick reply button. Clicking this button will end the Self-Service Thread.',
    path: 'nodesMap.next_steps.ivrNode.menu[0].label',
    label: 'When the customer doesn’t need more help',
    defaultValue: 'Thanks, I’m all set',
    placeholder: 'Message',
  },

  ASK_THERE_ANYTHING_ELSE_YOU_NEED_QUICK_REPLY_TO_HERO: {
    description: 'This will appear as a quick reply button.',
    path: 'nodesMap.next_steps.ivrNode.menu[1].label',
    label: 'When the customer needs more help',
    placeholder: 'Message',
    defaultValue: 'Speak to an agent',
  },

  CUSTOMER_HAS_OPTED_TO_END: {
    path: 'nodesMap.goodbye.ivrNode.message.message',
    label: 'Closing message when the customer doesn’t need more help',
    description: '',
    placeholder: 'Message',
    defaultValue: 'Feel free to chat again if you have more questions. Have a great day!',
  },
  CUSTOMER_HAS_OPTED_TO_HERO: {
    path: 'nodesMap.wait_route_to_hero.ivrNode.message.message',
    label: 'Closing message when the customer needs more help',
    description: '',
    placeholder: 'Message',
    defaultValue: 'Got it! Let’s connect you with someone who can help.',
  },
  ERROR: {
    path: 'nodesMap.error.ivrNode.message.message',
    label: 'When an authentication or order lookup error has occurred',
    description: '',
    placeholder: 'Message',
    defaultValue:
      'Sorry, something has gone wrong. But don’t worry, one of our agents will be with you shortly to help.',
  },
});
